import { useMemo, useState } from 'react'
import { Film, LayoutGrid, List, Search, X } from 'lucide-react'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore, type AssetBinSort } from '../../../store/uiStore'
import { assetDisplayName } from '../../../lib/assetName'
import { EmptyState } from '../../common/States'
import { UploadDropzone } from './UploadDropzone'
import { AssetCard } from './AssetCard'

const SORT_LABEL: Record<AssetBinSort, string> = {
  added: '追加順',
  name: '名前順',
  duration: '尺順',
}

export function AssetBin() {
  const panelWidth = useUiStore((s) => s.panelSizes.left)
  const { assets, assetOrder, uploads, uploadFiles } = useEditorStore()
  const viewMode = useUiStore((s) => s.assetBinViewMode)
  const setViewMode = useUiStore((s) => s.setAssetBinViewMode)
  const query = useUiStore((s) => s.assetBinQuery)
  const setQuery = useUiStore((s) => s.setAssetBinQuery)
  const sort = useUiStore((s) => s.assetBinSort)
  const setSort = useUiStore((s) => s.setAssetBinSort)

  const [dragOver, setDragOver] = useState(false)

  const uploadList = Object.values(uploads).filter((u) => u.status !== 'done')

  const visibleIds = useMemo(() => {
    let ids = assetOrder.filter((id) => assets[id])
    const q = query.trim().toLowerCase()
    // P10-2 (docs/P10_PHASE2.md §2 item 2): "検索・名前順ソートも表示名で" —
    // a renamed asset (`label` set) is found/sorted by its DISPLAY name, not
    // the underlying immutable `filename`.
    if (q) ids = ids.filter((id) => assetDisplayName(assets[id]).toLowerCase().includes(q))
    if (sort === 'name') {
      ids = [...ids].sort((a, b) => assetDisplayName(assets[a]).localeCompare(assetDisplayName(assets[b]), 'ja'))
    } else if (sort === 'duration') {
      ids = [...ids].sort((a, b) => assets[b].durationSec - assets[a].durationSec)
    }
    // 'added' = assetOrder のまま（挿入順）
    return ids
  }, [assetOrder, assets, query, sort])

  function isFileDrag(e: React.DragEvent) {
    return Array.from(e.dataTransfer.types).includes('Files')
  }

  return (
    <aside
      style={{ width: panelWidth }}
      className={`shrink-0 border-r flex flex-col bg-ink-surface/40 min-h-0 transition-colors ${
        dragOver ? 'border-accent-blue ring-1 ring-inset ring-accent-blue' : 'border-ink-border'
      }`}
      data-testid="asset-bin"
      onDragOver={(e) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={(e) => {
        if (!isFileDrag(e)) return
        setDragOver(false)
      }}
      onDrop={(e) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.files.length > 0) uploadFiles(Array.from(e.dataTransfer.files))
      }}
    >
      <div className="px-3 py-2.5 border-b border-ink-border space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-text-muted">素材ビン</div>
          <div className="flex items-center rounded border border-ink-border overflow-hidden" data-testid="asset-bin-view-toggle">
            <button
              onClick={() => setViewMode('grid')}
              title="グリッド表示"
              data-testid="asset-bin-view-grid"
              aria-pressed={viewMode === 'grid'}
              className={`p-1 ${viewMode === 'grid' ? 'bg-accent-blue/20 text-accent-blue' : 'text-text-faint hover:text-text-muted'}`}
            >
              <LayoutGrid size={13} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              title="リスト表示"
              data-testid="asset-bin-view-list"
              aria-pressed={viewMode === 'list'}
              className={`p-1 ${viewMode === 'list' ? 'bg-accent-blue/20 text-accent-blue' : 'text-text-faint hover:text-text-muted'}`}
            >
              <List size={13} />
            </button>
          </div>
        </div>

        <UploadDropzone />

        <div className="flex items-center gap-1.5">
          <div className="relative flex-1 min-w-0">
            <Search size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-text-faint" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="名前で検索"
              data-testid="asset-bin-search"
              className="w-full rounded bg-ink-bg/60 border border-ink-border pl-6 pr-6 py-1 text-[11px] text-text placeholder:text-text-faint focus:outline-none focus:border-accent-blue/60"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                title="クリア"
                className="absolute right-1 top-1/2 -translate-y-1/2 text-text-faint hover:text-text-muted"
              >
                <X size={11} />
              </button>
            )}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as AssetBinSort)}
            data-testid="asset-bin-sort"
            className="rounded bg-ink-bg/60 border border-ink-border px-1 py-1 text-[11px] text-text focus:outline-none focus:border-accent-blue/60"
          >
            {(Object.keys(SORT_LABEL) as AssetBinSort[]).map((s) => (
              <option key={s} value={s}>
                {SORT_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {uploadList.length > 0 && (
        <div className="px-3 py-2 border-b border-ink-border space-y-1.5">
          {uploadList.map((u) => (
            <div key={u.id} className="text-[10px]" data-testid="upload-progress">
              <div className="flex justify-between text-text-faint mb-0.5">
                <span className="truncate max-w-[160px]">{u.fileName}</span>
                <span>{u.status === 'processing' ? '取込中' : `${Math.round((u.loaded / u.total) * 100)}%`}</span>
              </div>
              <div className="h-1 rounded bg-ink-bg overflow-hidden">
                <div
                  className={`h-full ${u.status === 'error' ? 'bg-accent-red' : 'bg-accent-blue'} transition-all`}
                  style={{
                    width:
                      u.status === 'processing'
                        ? '100%'
                        : `${Math.min(100, Math.round((u.loaded / u.total) * 100))}%`,
                  }}
                />
              </div>
              {u.status === 'error' && <div className="text-accent-red mt-0.5">{u.error}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2" data-testid="asset-bin-scroll">
        {assetOrder.length === 0 ? (
          <EmptyState
            icon={<Film size={26} />}
            title="素材がまだありません"
            description="ここに動画をドロップ、または上のボタンから追加してください。自動でシーン検出されます。"
          />
        ) : visibleIds.length === 0 ? (
          <div className="text-[11px] text-text-faint text-center py-8" data-testid="asset-bin-no-match">
            「{query}」に一致する素材がありません
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-2" data-testid="asset-bin-grid">
            {visibleIds.map((id) => (
              <AssetCard key={id} asset={assets[id]} />
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="asset-bin-list">
            {visibleIds.map((id) => (
              <AssetCard key={id} asset={assets[id]} />
            ))}
          </div>
        )}
      </div>
    </aside>
  )
}
